import {
  filterCityItems,
  type CurationFilterableItem,
  type CurationFiltersState,
} from "@/components/pages/guesteria-partners/utils/guesteriaCurationFilters";

export type ActivityStatusFilter = "all" | "active" | "hidden";

export type ActivitySortField = "name" | "location" | "top" | "status";

export type ActivityTableRow = {
  productCode: string;
  title: string;
  location: string;
  /** TOP rank from the curated list. */
  position: number;
  hidden: boolean;
  /** Product tags/categories; searched together with the title. */
  tags?: readonly string[];
};

/** Search by title or tag (case-insensitive, trimmed) and status filter. */
export function filterActivityRows<T extends ActivityTableRow>(
  rows: readonly T[],
  search: string,
  status: ActivityStatusFilter,
): T[] {
  const needle = search.trim().toLowerCase();
  return rows.filter((row) => {
    if (status === "active" && row.hidden) return false;
    if (status === "hidden" && !row.hidden) return false;
    if (needle === "") return true;
    return (
      row.title.toLowerCase().includes(needle) ||
      (row.tags ?? []).some((tag) => tag.toLowerCase().includes(needle))
    );
  });
}

function compare(a: ActivityTableRow, b: ActivityTableRow, field: string) {
  switch (field) {
    case "name":
      return a.title.localeCompare(b.title, undefined, { sensitivity: "base" });
    case "location":
      return a.location.localeCompare(b.location, undefined, {
        sensitivity: "base",
      });
    case "top":
      return a.position - b.position;
    case "status":
      return Number(a.hidden) - Number(b.hidden);
    default:
      return 0;
  }
}

/** Stable local sort; unknown field or no direction keeps the input order. */
export function sortActivityRows<T extends ActivityTableRow>(
  rows: readonly T[],
  field: string | null,
  direction: "asc" | "desc" | null,
): T[] {
  if (!field || !direction) return [...rows];
  const sign = direction === "asc" ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index }))
    .sort((x, y) => {
      const c = compare(x.row, y.row, field) * sign;
      return c !== 0 ? c : x.index - y.index;
    })
    .map((entry) => entry.row);
}

/** Search + status + the popover filters (visibility, source, price), all ANDed. */
export function filterActivityTableRows<
  T extends ActivityTableRow & CurationFilterableItem,
>(
  rows: readonly T[],
  search: string,
  status: ActivityStatusFilter,
  curationFilters: CurationFiltersState,
): T[] {
  return filterCityItems(
    filterActivityRows(rows, search, status),
    curationFilters,
  );
}
